import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Camera, Image as ImageIcon, Link as LinkIcon, Mic, Sparkles, X } from "lucide-react";
import { useBurrow } from "./BurrowStore";
import { haptic } from "../lib/haptics";
import { ResponsiveSheet } from "./ResponsiveSheet";

interface QuickCaptureSheetProps {
  open: boolean;
  onClose: () => void;
  /** Prefill from the share target (/capture?title=&text=&url=). */
  initialText?: string;
  initialTitle?: string;
}

const URL_PATTERN = /^https?:\/\/[^\s]+$/;

// Unsaved-draft persistence (like an unsent message): survives closing the
// sheet and navigating/reloading until the thought is Kept. Only the typed
// text + title are persisted — an attached image isn't restored after a hard reload.
const DRAFT_KEY = "burrow:quick-capture-draft";
type Draft = { text?: string; title?: string };
function loadDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}
function saveDraft(d: Draft) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* storage unavailable — non-fatal */
  }
}
function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* non-fatal */
  }
}

/**
 * The mobile equivalent of the Chrome extension: one sheet, one thumb,
 * capture a thought / link / photo in seconds. Opens from the tab-bar FAB
 * and the /capture share target.
 */
export function QuickCaptureSheet({ open, onClose, initialText, initialTitle }: QuickCaptureSheetProps) {
  const { addThought, addImageThought, showToast } = useBurrow();
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  // Photos are a list: one photo becomes one card, so selecting several creates
  // several cards at once. `filePreviews[i]` is the object URL for `files[i]`.
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const textBeforeRecordingRef = useRef("");
  // Whether any of this thought was dictated; saved as the card's capture_method.
  const usedVoiceRef = useRef(false);

  // On open: a share-target capture seeds from its params; otherwise restore any
  // unsaved draft so a half-typed thought survives closing/navigating away.
  useEffect(() => {
    if (!open) return;
    const isShare = initialText !== undefined || initialTitle !== undefined;
    if (isShare) {
      setText(initialText ?? "");
      setTitle(initialTitle ?? "");
    } else {
      const draft = loadDraft();
      if (draft) {
        setText(draft.text ?? "");
        setTitle(draft.title ?? "");
      }
    }
    const t = setTimeout(() => textareaRef.current?.focus(), 250);
    return () => clearTimeout(t);
  }, [open, initialText, initialTitle]);

  // Persist the draft as you type; clear it once there's nothing left to keep.
  useEffect(() => {
    if (!open) return;
    if (text.trim() || title.trim()) saveDraft({ text, title });
    else clearDraft();
  }, [text, title, open]);

  // Revoke every object URL when the sheet unmounts so we don't leak blobs.
  useEffect(() => {
    return () => {
      filePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [filePreviews]);

  const detectedUrl = URL_PATTERN.test(text.trim()) ? text.trim() : null;
  const canSave = !!text.trim() || files.length > 0;

  const reset = () => {
    setText("");
    setTitle("");
    setFiles([]);
    setFilePreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    recognitionRef.current?.stop();
    setIsRecording(false);
    usedVoiceRef.current = false;
  };

  const removePhotoAt = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Closing without Keeping preserves the draft (persisted above) so it can be
  // resumed later — only stop any in-progress recording.
  const handleClose = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
    onClose();
  };

  const handleSave = () => {
    if (!canSave) return;
    haptic("success");
    const body = text.trim();
    const titleValue = title.trim() || undefined;
    const captureMethod = usedVoiceRef.current ? "dictated" : "typed";
    if (files.length === 1) {
      // Single photo → one card, carrying any body/title/link the user added.
      addImageThought(files[0], {
        body: body || undefined,
        title: titleValue,
        sourceUrl: detectedUrl ?? undefined,
        sourceTitle: detectedUrl ? new URL(detectedUrl).hostname : undefined,
      });
    } else if (files.length > 1) {
      // Multiple photos → one card per photo. Any body/title is shared across
      // them as a common caption so nothing the user typed is lost.
      files.forEach((f) =>
        addImageThought(f, { body: body || undefined, title: titleValue, source: "quick_capture" })
      );
      // Overrides the per-card "Thought kept." toasts with one summary.
      showToast(`${files.length} thoughts kept.`);
    } else if (detectedUrl) {
      addThought(body, titleValue, detectedUrl, new URL(detectedUrl).hostname, "quick_capture", undefined, captureMethod);
    } else {
      addThought(body, titleValue, undefined, undefined, "quick_capture", undefined, captureMethod);
    }
    // Kept — discard the draft and clear the composer.
    clearDraft();
    reset();
    onClose();
  };

  // Accepts one or many files (multi-select from the gallery). Non-images are
  // skipped with a nudge; valid photos are appended to the pending list.
  const MAX_PHOTOS = 20;
  const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // mirrors addImageThought's limit
  const pickFiles = (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    const incoming = Array.from(picked);
    const images = incoming.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) {
      showToast("Only images are allowed (JPG, PNG, GIF, WebP).");
      return;
    }
    // Reject oversized photos now so they don't get silently dropped at save.
    const withinSize = images.filter((f) => f.size <= MAX_PHOTO_BYTES);
    const room = Math.max(0, MAX_PHOTOS - files.length);
    const accepted = withinSize.slice(0, room);
    if (accepted.length === 0) {
      if (withinSize.length === 0) showToast("Each photo must be under 5 MB.");
      else showToast(`You can add up to ${MAX_PHOTOS} photos at once.`);
      return;
    }
    const urls = accepted.map((f) => URL.createObjectURL(f));
    setFiles((prev) => [...prev, ...accepted]);
    setFilePreviews((prev) => [...prev, ...urls]);
    // Tell the user if anything was left out, most-specific reason first.
    if (withinSize.length > room) showToast(`You can add up to ${MAX_PHOTOS} photos at once.`);
    else if (withinSize.length < images.length) showToast("Some photos were over 5 MB and skipped.");
    else if (images.length < incoming.length) showToast("Some files were skipped — only images are allowed.");
  };

  const toggleVoice = () => {
    const SpeechRecognitionAPI =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      showToast("Voice input is not supported in this browser.");
      return;
    }
    haptic("light");
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }
    textBeforeRecordingRef.current = text;
    const recognition: any = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (e: any) => {
      // Append ONLY newly-finalized results (tracked via e.resultIndex) to the
      // running text, so a phrase is added exactly once and never repeats.
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          const transcript = e.results[i][0].transcript.trim();
          textBeforeRecordingRef.current +=
            (textBeforeRecordingRef.current ? " " : "") + transcript;
          setText(textBeforeRecordingRef.current);
        }
      }
    };
    recognition.onend = () => setIsRecording(false);
    recognition.onerror = () => {
      setIsRecording(false);
      showToast("Voice input failed. Try again.");
    };
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    usedVoiceRef.current = true;
  };

  return (
    <ResponsiveSheet open={open} onClose={handleClose} label="Capture a thought" desktop="center" zIndex={70}>
      <div className="p-5 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-accent" />
            <h2
              className="text-primary"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.05rem", fontWeight: 400 }}
            >
              Capture
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-11 h-11 -m-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Title — optional, always typeable: tap and write. Left empty it's
            never stored (handleSave uses `|| undefined`). */}
        <input
          ref={titleRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              textareaRef.current?.focus();
            }
          }}
          placeholder="Untitled"
          className="w-full mb-2 px-3.5 py-2.5 rounded-xl border border-border/30 bg-background text-foreground placeholder:text-muted-foreground/30 placeholder:italic outline-none focus:border-accent/30 transition-colors"
          style={{ fontSize: "0.95rem", fontFamily: "'Fraunces', Georgia, serif" }}
        />

        {/* Body */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSave();
            }
          }}
          placeholder={isRecording ? "Listening..." : "What's on your mind? Paste a link, type a thought..."}
          rows={4}
          className="w-full px-3.5 py-3 rounded-xl border border-border/30 bg-background text-foreground placeholder:text-muted-foreground/30 outline-none focus:border-accent/30 resize-none transition-colors"
          style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.95rem", lineHeight: "1.65" }}
        />

        {/* Link detection */}
        <AnimatePresence>
          {detectedUrl && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="flex items-center gap-2 mt-2 px-3 py-2 rounded-xl bg-accent/8 border border-accent/15"
            >
              <LinkIcon size={13} className="text-accent shrink-0" />
              <span className="text-accent/80 truncate" style={{ fontSize: "0.78rem" }}>
                Link detected: {new URL(detectedUrl).hostname}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Image preview(s) — one photo shows large; several show a thumbnail
            grid, each photo becoming its own card on Keep. */}
        <AnimatePresence>
          {filePreviews.length === 1 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              className="relative mt-3 rounded-xl overflow-hidden border border-border/30"
            >
              <img src={filePreviews[0]} alt="" className="w-full object-cover" style={{ maxHeight: 200 }} />
              <button
                onClick={() => removePhotoAt(0)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-foreground/60 text-background backdrop-blur-sm"
                aria-label="Remove image"
              >
                <X size={14} />
              </button>
            </motion.div>
          )}
          {filePreviews.length > 1 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              className="mt-3"
            >
              <div className="grid grid-cols-3 gap-2">
                {filePreviews.map((url, i) => (
                  <div key={url} className="relative rounded-lg overflow-hidden border border-border/30 aspect-square">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhotoAt(i)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-foreground/60 text-background backdrop-blur-sm"
                      aria-label={`Remove photo ${i + 1}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-muted-foreground/60" style={{ fontSize: "0.75rem" }}>
                {filePreviews.length} photos → {filePreviews.length} cards, one each.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Capture modes + save */}
        <div className="flex items-center justify-between gap-2 mt-4">
          <div className="flex items-center gap-1">
            <button
              onClick={() => galleryRef.current?.click()}
              className="p-3 rounded-xl text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all"
              title="Add photos"
              aria-label="Add photos"
            >
              <ImageIcon size={19} />
            </button>
            <button
              onClick={() => cameraRef.current?.click()}
              className="p-3 rounded-xl text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all md:hidden"
              title="Take a photo"
              aria-label="Take a photo"
            >
              <Camera size={19} />
            </button>
            <button
              onClick={toggleVoice}
              className={`p-3 rounded-xl transition-all ${
                isRecording
                  ? "bg-accent text-accent-foreground shadow-lg shadow-accent/20 animate-pulse"
                  : "text-muted-foreground hover:text-accent hover:bg-accent/10"
              }`}
              title={isRecording ? "Stop recording" : "Voice input"}
              aria-label={isRecording ? "Stop recording" : "Voice input"}
            >
              <Mic size={19} />
            </button>
          </div>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`px-6 py-3 rounded-xl transition-all ${
              canSave
                ? "bg-accent text-accent-foreground hover:opacity-90 shadow-lg shadow-accent/20"
                : "bg-muted/20 text-muted-foreground/30 cursor-not-allowed"
            }`}
            style={{ fontSize: "0.9rem", fontFamily: "'Fraunces', Georgia, serif" }}
          >
            Keep
          </button>
        </div>

        <span className="hint-keyboard block mt-3 text-muted-foreground/40" style={{ fontSize: "0.72rem" }}>
          Ctrl+Enter to save
        </span>

        {/* Hidden pickers: gallery (multi-select) + rear camera (one shot) */}
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            pickFiles(e.target.files);
            e.currentTarget.value = "";
          }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            pickFiles(e.target.files);
            e.currentTarget.value = "";
          }}
        />
      </div>
    </ResponsiveSheet>
  );
}
